import { Banner } from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { getMe } from "../api/me";
import { QueryErrorBanner } from "./QueryErrorBanner";

import { t } from "../lib/i18n";

interface GatedQueryErrorProps {
  /** 取得に失敗したデータ名（例: "在庫分析"）。上限超過時の案内では使わない。 */
  title?: string;
  /** React Query の refetch。上限超過ではない失敗時のみ「再試行」で使う。 */
  onRetry: () => void;
}

/**
 * 商品数上限で 403 になる機能ページ（dashboard/発注書/発注点/在庫分析/セット商品/
 * 資金繰り）のデータ取得失敗を分岐する共通コンポーネント。
 *
 * 上限超過（バックエンドの product-limit 403）のときは「取得に失敗しました＋再試行」を
 * 出さない——再試行しても必ず 403 で失敗し、サーバー障害のように見えるため。代わりに
 * 上限超過の案内とアップグレード導線を出す（上部の常設 ProductLimitBanner と役割は同じだが、
 * ウィジェットの跡地を行き止まりにしないための inline 版）。
 *
 * それ以外の失敗（Fly 起床中など一時的なもの）は従来どおり QueryErrorBanner に委ねる。
 * ["me"] クエリは ProductLimitBanner / 各ページと共有するため追加フェッチは発生しない。
 */
export function GatedQueryError({ title, onRetry }: GatedQueryErrorProps) {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const productLimit = data?.product_limit;

  if (productLimit?.over_limit && productLimit.limit !== null) {
    return (
      <Banner
        tone="warning"
        title={t("商品数がプランの上限を超えているため表示できません")}
        action={{ content: t("プランを見る"), onAction: () => navigate("/plans") }}
      >
        <p>
          {t("アップグレードすると解放されます。データの同期は継続しているため、アップグレード後はそのまま最新の内容が表示されます。")}
        </p>
      </Banner>
    );
  }

  return <QueryErrorBanner title={title} onRetry={onRetry} />;
}
